'use client';

/* eslint-disable @next/next/no-img-element -- Library assets may live on arbitrary user-provided URLs. */

// Galexia Asset Library — the persistent media_assets surface.
//
// Design language (see reference/images: Higgsfield Supercomputer +
// Leonardo.ai All Assets): a floating rounded-2xl panel on a near-black
// canvas, an in-page left rail with per-type counts, a slim backdrop-blur
// header with pill controls and a density slider, one accent (violet) used
// sparingly, gradient uppercase display heading with a soft glow blob, and a
// centered stacked-cards empty state.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ai-elements/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDocumentTitle } from '@/lib/use-document-title';
import {
  Archive,
  ArrowSquareOut,
  Check,
  Copy,
  FileAudio,
  FileDoc,
  FileSvg,
  FolderSimple,
  ImageSquare,
  LinkSimple,
  MagnifyingGlass,
  Plus,
  Sparkle,
  SquaresFour,
  Stack,
  UploadSimple,
  VideoCamera,
} from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LibraryAsset {
  id: string;
  type: string;
  role: string;
  title: string;
  description: string | null;
  fileUrl: string;
  thumbnailUrl: string | null;
  mimeType: string;
  sizeBytes: number;
  filename: string | null;
  sourceType: string;
  sourceUrl: string | null;
  tags: string[] | null;
  createdAt: string;
}

const ASSET_TYPES = [
  { id: 'image', label: 'Images', icon: ImageSquare },
  { id: 'video', label: 'Videos', icon: VideoCamera },
  { id: 'audio', label: 'Audio', icon: FileAudio },
  { id: 'logo', label: 'Logos', icon: Sparkle },
  { id: 'svg', label: 'SVGs', icon: FileSvg },
  { id: 'document', label: 'Documents', icon: FileDoc },
  { id: 'other', label: 'Other', icon: Stack },
] as const;

const ASSET_ROLES = ['reference', 'brand', 'product', 'character', 'background', 'logo', 'style', 'source'] as const;
type AssetRole = (typeof ASSET_ROLES)[number];

type Density = 'compact' | 'cozy' | 'roomy';
const DENSITY_GRID: Record<Density, string> = {
  compact: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6',
  cozy: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
  roomy: 'grid-cols-1 md:grid-cols-2 2xl:grid-cols-3',
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function AssetThumb({ asset }: { asset: LibraryAsset }) {
  const url = asset.thumbnailUrl || asset.fileUrl;
  if (asset.type === 'image' || asset.type === 'logo' || asset.type === 'svg') {
    return <img src={url} alt={asset.title} className="h-full w-full object-cover" loading="lazy" />;
  }
  if (asset.type === 'video') {
    return <video src={asset.fileUrl} muted playsInline preload="metadata" className="h-full w-full object-cover" />;
  }
  const Icon = ASSET_TYPES.find((t) => t.id === asset.type)?.icon ?? Stack;
  return (
    <div className="grid h-full w-full place-items-center bg-muted/30">
      <Icon weight="duotone" className="size-9 text-muted-foreground/50" />
    </div>
  );
}

/** Leonardo-style stacked, tilted preview cards for the empty state. */
function EmptyStackIllustration() {
  return (
    <div className="relative mx-auto h-24 w-44" aria-hidden>
      {[
        { rotate: '-rotate-12', translate: '-translate-x-14', gradient: 'from-violet-500/50 to-indigo-500/30' },
        { rotate: '-rotate-3', translate: '-translate-x-5', gradient: 'from-fuchsia-500/40 to-violet-500/30' },
        { rotate: 'rotate-3', translate: 'translate-x-5', gradient: 'from-indigo-500/40 to-sky-500/30' },
        { rotate: 'rotate-12', translate: 'translate-x-14', gradient: 'from-violet-400/50 to-purple-500/30' },
      ].map((card, i) => (
        <div
          key={i}
          className={`absolute left-1/2 top-1/2 -ml-8 h-20 w-16 -translate-y-1/2 ${card.translate} ${card.rotate} rounded-lg border border-white/10 bg-gradient-to-br ${card.gradient} shadow-lg backdrop-blur-sm`}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LibraryClient() {
  useDocumentTitle('Library');
  const [assets, setAssets] = useState<LibraryAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [density, setDensity] = useState<Density>('cozy');
  const [copiedAssetId, setCopiedAssetId] = useState<string | null>(null);

  // Save dialog state
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveRole, setSaveRole] = useState<AssetRole>('reference');
  const [saveTitle, setSaveTitle] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveTags, setSaveTags] = useState('');
  const [saveUrls, setSaveUrls] = useState('');
  const [saveFiles, setSaveFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAssets = useCallback(async (search = '', type = 'all', role = 'all') => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (search.trim()) params.set('query', search.trim());
      if (type !== 'all') params.set('type', type);
      if (role !== 'all') params.set('role', role);
      const response = await fetch(`/api/media/assets?${params}`);
      if (!response.ok) throw new Error(`Failed to load assets (${response.status})`);
      const data = await response.json();
      setAssets(Array.isArray(data.assets) ? data.assets : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const applyFilters = (type: string, role: string) => {
    setTypeFilter(type);
    setRoleFilter(role);
    fetchAssets(query, type, role);
  };

  const handleSearchChange = (value: string) => {
    setQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => fetchAssets(value, typeFilter, roleFilter), 350);
  };

  // Per-type counts for the rail, computed from the loaded set.
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const asset of assets) counts.set(asset.type, (counts.get(asset.type) ?? 0) + 1);
    return counts;
  }, [assets]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const urls = saveUrls.split(/\n|,/).map((u) => u.trim()).filter(Boolean);
      if (saveFiles.length === 0 && urls.length === 0) {
        throw new Error('Add at least one file or link.');
      }
      const formData = new FormData();
      for (const file of saveFiles.slice(0, 20)) formData.append('file', file);
      for (const url of urls.slice(0, 20)) formData.append('url', url);
      formData.set('role', saveRole);
      if (saveTitle.trim()) formData.set('title', saveTitle.trim());
      if (saveDescription.trim()) formData.set('description', saveDescription.trim());
      if (saveTags.trim()) formData.set('tags', saveTags);

      const response = await fetch('/api/media/assets', { method: 'POST', body: formData });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Save failed (${response.status})`);
      }
      const savedCount = Array.isArray(data?.assets) ? data.assets.length : 1;
      setNotice(`Saved ${savedCount} asset${savedCount === 1 ? '' : 's'} to your library.`);
      setSaveOpen(false);
      setSaveFiles([]);
      setSaveUrls('');
      setSaveTitle('');
      setSaveDescription('');
      setSaveTags('');
      fetchAssets(query, typeFilter, roleFilter);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (asset: LibraryAsset) => {
    if (!window.confirm(`Archive “${asset.title}”? It will be hidden from your library.`)) return;
    try {
      const response = await fetch(`/api/media/assets?id=${encodeURIComponent(asset.id)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Archive failed (${response.status})`);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Archive failed');
    }
  };

  const handleCopy = async (asset: LibraryAsset) => {
    await navigator.clipboard.writeText(asset.fileUrl);
    setCopiedAssetId(asset.id);
    setTimeout(() => setCopiedAssetId((prev) => (prev === asset.id ? null : prev)), 1500);
  };

  const totalCount = assets.length;

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      {/* Floating panel */}
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">

        {/* Sticky header */}
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <h1 className="text-sm font-semibold tracking-wide">Library</h1>
          <div className="relative ml-2 min-w-[200px] max-w-xs flex-1">
            <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
            <input
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search titles, tags, filenames…"
              className="h-8 w-full rounded-full border border-border/80 bg-background pl-9 pr-4 text-xs font-medium outline-none transition-colors hover:bg-muted/30 focus:border-violet-500/50"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Density control (Leonardo zoom slider) */}
            <div className="hidden items-center gap-0.5 rounded-full border border-border/80 bg-background p-0.5 sm:flex" role="group" aria-label="Grid density">
              {(['compact', 'cozy', 'roomy'] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setDensity(d)}
                  aria-label={`${d} grid`}
                  className={`grid size-7 place-items-center rounded-full transition-colors ${density === d ? 'bg-violet-500/15 text-violet-400' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <SquaresFour className={d === 'compact' ? 'size-3' : d === 'cozy' ? 'size-3.5' : 'size-4'} />
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-xs"
              onClick={() => { setSaveOpen(true); setSaveUrls(''); }}
            >
              <LinkSimple className="mr-1.5 size-3.5" />
              Import links
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-full bg-violet-500 px-3 text-xs font-semibold text-white hover:bg-violet-400"
              onClick={() => setSaveOpen(true)}
            >
              <Plus className="mr-1 size-3.5" weight="bold" />
              Save to library
            </Button>
          </div>
        </header>

        {/* Hero strip */}
        <section className="relative overflow-hidden border-b border-border/40 px-4 py-8 md:px-8">
          <div className="relative z-10 max-w-2xl">
            <h2 className="bg-gradient-to-b from-foreground to-muted-foreground/60 bg-clip-text text-xl font-extrabold uppercase tracking-tight text-transparent md:text-2xl">
              Your asset library
            </h2>
            <div className="mt-3 flex flex-col gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2.5">
                <Stack weight="duotone" className="size-4 shrink-0 text-violet-400" />
                <p><span className="font-medium text-foreground">Brand assets, references, and saved outputs</span> — reusable across chats and media generations.</p>
              </div>
              <div className="flex items-center gap-2.5">
                <Sparkle weight="duotone" className="size-4 shrink-0 text-violet-400" />
                <p><span className="font-medium text-foreground">Galexia sees this library</span> — the agent pulls from it when it writes, designs, and generates for you.</p>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute -top-8 right-8 h-40 w-72 rounded-full bg-violet-500/10 blur-[80px]" />
        </section>

        {/* Body: rail + grid */}
        <div className="flex min-h-0 flex-1">

          {/* Left rail */}
          <aside className="hidden w-52 shrink-0 flex-col gap-1 border-r border-border/40 p-3 md:flex">
            <button
              onClick={() => applyFilters('all', 'all')}
              className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${typeFilter === 'all' ? 'bg-violet-500/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'}`}
            >
              <span className="flex items-center gap-2.5"><Stack weight="duotone" className="size-4" /> All assets</span>
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">{loading ? '…' : totalCount}</span>
            </button>

            <p className="mt-4 px-3 text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">Types</p>
            {ASSET_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => applyFilters(t.id, roleFilter)}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${typeFilter === t.id ? 'bg-violet-500/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'}`}
              >
                <span className="flex items-center gap-2.5"><t.icon weight="duotone" className="size-4" /> {t.label}</span>
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                  {typeFilter === 'all' || typeFilter === t.id ? (typeCounts.get(t.id) ?? 0) : '·'}
                </span>
              </button>
            ))}

            <p className="mt-4 px-3 text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">Roles</p>
            <div className="flex flex-wrap gap-1 px-2">
              {(['all', ...ASSET_ROLES] as const).map((role) => (
                <button
                  key={role}
                  onClick={() => applyFilters(typeFilter, role)}
                  className={`rounded-full border px-2 py-0.5 text-[11px] capitalize transition-colors ${roleFilter === role ? 'border-violet-500/50 bg-violet-500/10 text-foreground' : 'border-border/70 text-muted-foreground hover:text-foreground'}`}
                >
                  {role}
                </button>
              ))}
            </div>

            <div className="mt-auto border-t border-border/40 pt-3">
              <Link href="/documents" className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground">
                <FolderSimple weight="duotone" className="size-4" />
                Documents workspace
              </Link>
            </div>
          </aside>

          {/* Main grid area */}
          <main className="min-w-0 flex-1 p-4 md:p-6">
            {notice && (
              <p className="mb-4 rounded-lg border border-violet-500/25 bg-violet-500/[0.06] px-3 py-2 text-xs text-violet-300">{notice}</p>
            )}
            {error && (
              <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{error}</p>
            )}

            {loading ? (
              <div className={`grid gap-4 ${DENSITY_GRID[density]}`}>
                {Array.from({ length: 8 }, (_, i) => (
                  <div key={i} className="overflow-hidden rounded-xl border border-border/60">
                    <Skeleton className="aspect-[4/3] w-full rounded-none" />
                    <div className="space-y-2 p-3">
                      <Skeleton className="h-3.5 w-3/4" />
                      <Skeleton className="h-2.5 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : assets.length === 0 ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                <EmptyStackIllustration />
                <p className="mt-8 text-base font-semibold">Your assets will appear here</p>
                <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
                  Upload files or copy pasted links into Galexia storage — assets are
                  reused across chats and media generations.
                </p>
                <Button
                  size="sm"
                  className="mt-6 rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
                  onClick={() => setSaveOpen(true)}
                >
                  <UploadSimple className="mr-1.5 size-4" />
                  Save your first asset
                </Button>
              </div>
            ) : (
              <>
                <p className="mb-4 text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground/60">
                  Saved items · {assets.length}
                </p>
                <div className={`grid gap-4 ${DENSITY_GRID[density]}`}>
                  {assets.map((asset) => (
                    <article
                      key={asset.id}
                      className="group overflow-hidden rounded-xl border border-border/60 bg-card/60 transition-all hover:border-violet-500/40 hover:shadow-[0_4px_24px_rgba(139,92,246,0.08)]"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <AssetThumb asset={asset} />
                        {/* Hover action bar */}
                        <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={() => handleCopy(asset)}
                            aria-label="Copy URL"
                            className="grid size-8 place-items-center rounded-full bg-white/10 text-white/85 backdrop-blur-sm transition-colors hover:bg-white/20"
                          >
                            {copiedAssetId === asset.id ? <Check className="size-4" /> : <Copy className="size-4" />}
                          </button>
                          <a
                            href={asset.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Open original"
                            className="grid size-8 place-items-center rounded-full bg-white/10 text-white/85 backdrop-blur-sm transition-colors hover:bg-white/20"
                          >
                            <ArrowSquareOut className="size-4" />
                          </a>
                          <button
                            onClick={() => handleArchive(asset)}
                            aria-label="Archive"
                            className="grid size-8 place-items-center rounded-full bg-white/10 text-white/85 backdrop-blur-sm transition-colors hover:bg-white/20"
                          >
                            <Archive className="size-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-medium">{asset.title}</p>
                          <span className="shrink-0 rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                            {asset.role}
                          </span>
                        </div>
                        {asset.tags && asset.tags.length > 0 && (
                          <p className="truncate text-xs text-muted-foreground/70">
                            {asset.tags.slice(0, 5).map((tag) => `#${tag}`).join(' ')}
                          </p>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Save-to-library dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Save to library</DialogTitle>
            <DialogDescription>
              Upload files or copy pasted links into Galexia storage. Assets become
              reusable references for chats and media generations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Files</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.svg"
                onChange={(e) => setSaveFiles(Array.from(e.target.files ?? []))}
                className="block w-full cursor-pointer rounded-lg border border-dashed border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-violet-500/15 file:px-3 file:py-1 file:text-xs file:font-medium file:text-violet-400"
              />
              {saveFiles.length > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">{saveFiles.length} file{saveFiles.length === 1 ? '' : 's'} selected</p>
              )}
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Links</p>
              <textarea
                value={saveUrls}
                onChange={(e) => setSaveUrls(e.target.value)}
                placeholder={'https://example.com/logo.png\nhttps://example.com/hero.mp4'}
                rows={3}
                className="w-full rounded-lg border bg-background px-3 py-2 text-xs outline-none transition-colors focus:border-violet-500/50"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Role</p>
                <select
                  value={saveRole}
                  onChange={(e) => setSaveRole(e.target.value as AssetRole)}
                  className="h-9 w-full rounded-lg border bg-background px-2 text-sm capitalize outline-none"
                >
                  {ASSET_ROLES.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Title (optional)</p>
                <Input value={saveTitle} onChange={(e) => setSaveTitle(e.target.value)} placeholder="NaviSmart brand logo" />
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Description (optional)</p>
              <Input value={saveDescription} onChange={(e) => setSaveDescription(e.target.value)} placeholder="Primary logo for ad generation" />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Tags (comma separated)</p>
              <Input value={saveTags} onChange={(e) => setSaveTags(e.target.value)} placeholder="brand, navismart, product" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setSaveOpen(false)}>Cancel</Button>
              <Button
                size="sm"
                disabled={saving}
                onClick={handleSave}
                className="rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
              >
                {saving ? 'Saving…' : 'Save to library'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
